import { MusicData } from "@/common/types/MasterData";
import { LATEST_VERSION, Version, VERSION_STRING } from "@/common/types/Version";
import Chart from "@/models/Chart";
import { PLAY_STYLE, PlayStyle } from "@/common/types/PlayStyle";
import { DIFFICULTY, Difficulty } from "@/common/types/Difficulty";

export default class Music {
  readonly id: number;
  readonly revision: number;
  readonly title: string;
  readonly genre: string;
  readonly artist: string;
  readonly version: Version;
  readonly minBpm: number;
  readonly maxBpm: number;
  private readonly _textage: string | null;
  readonly includedVersion: Version;
  readonly deletedVersion: Version | null;
  private readonly _otherRevisions: Map<number, Music>;
  private readonly charts: Record<PlayStyle, Record<Difficulty, Chart | undefined>>;

  constructor(data: MusicData, otherRevisions: Map<number, Music>) {
    this.id = data.id;
    this.revision = data.revision;
    this.title = data.title;
    this.genre = data.genre;
    this.artist = data.artist;
    this.version = data.version;
    this.minBpm = data.minBpm;
    this.maxBpm = data.maxBpm;
    this._textage = data.textage;
    this.includedVersion = data.includedVersion;
    this.deletedVersion = data.deletedVersion;
    this._otherRevisions = otherRevisions;
    this.charts = {
      [PLAY_STYLE.SP]: {
        [DIFFICULTY.BEGINNER]: undefined,
        [DIFFICULTY.NORMAL]: undefined,
        [DIFFICULTY.HYPER]: undefined,
        [DIFFICULTY.ANOTHER]: undefined,
        [DIFFICULTY.LEGGENDARIA]: undefined,
      },
      [PLAY_STYLE.DP]: {
        [DIFFICULTY.BEGINNER]: undefined,
        [DIFFICULTY.NORMAL]: undefined,
        [DIFFICULTY.HYPER]: undefined,
        [DIFFICULTY.ANOTHER]: undefined,
        [DIFFICULTY.LEGGENDARIA]: undefined,
      },
    };
  }

  get versionString(): string {
    return VERSION_STRING[this.version];
  }

  get isSoflan(): boolean {
    return this.minBpm !== this.maxBpm;
  }

  get bpmString(): string {
    return this.isSoflan ? `${this.minBpm}-${this.maxBpm}` : this.minBpm.toString();
  }

  get textage(): string | null {
    if (this._textage === null) return null;

    let v: string = this.version.toString();
    if (v === "1") {
      v = "s";
    } else if (v === "0") {
      v = "1";
    }

    return `https://textage.cc/score/${v}/${this._textage}.html`;
  }

  get includedVersionString(): string {
    return VERSION_STRING[this.includedVersion];
  }

  get lastIncludedVersionString(): string | null {
    return this.deletedVersion ? VERSION_STRING[(this.deletedVersion - 1) as Version] : null;
  }

  get deletedVersionString(): string | null {
    return this.deletedVersion ? VERSION_STRING[this.deletedVersion] : null;
  }

  get isDeleted(): boolean {
    return this.deletedVersion !== null && this.deletedVersion <= LATEST_VERSION;
  }

  get isOldRevision(): boolean {
    return this.revision !== Math.max(...this._otherRevisions.keys());
  }

  chart(chart: Chart) {
    this.charts[chart.playStyle][chart.difficulty] = chart;
  }

  mostDifficultChart(playStyle: PlayStyle) {
    return Object.values(DIFFICULTY)
      .sort()
      .reverse()
      .map((e) => this.charts[playStyle][e])
      .find((e) => e);
  }
}
